import { useEffect, useState } from 'react'

import Action from 'ageas-react-component-action'
import CheckBox from 'ageas-react-component-checkbox'
import Dropdown from 'ageas-react-component-dropdownlist'
import Link from 'ageas-react-component-link'
import MainLoader from 'ageas-react-component-loader'
import Section from 'ageas-react-component-section'
import TextField from 'ageas-react-component-textfield'

import moment from 'moment'
import { Result } from '../../@types/PolicyHolderDetails'
import { usePolicyholderDetailsStore } from '../../Stores/policyholderDetailsStore'
import { useGetAdditionalEntityDetailsStore } from '../../Stores/useGetAdditionalEntityDetailsStore'
import { useLoadPolicyHolderReferenceDataStore } from '../../Stores/useLoadPolicyHolderReferenceDataStore'
import { useWarningStore } from '../../Stores/warningStore'
import { ADRUM_EVENT } from '../../constants/adrumEvent'
import { WARNING_BAR } from '../../constants/warningBar'
import { useIdentificationUpdateEntityPayload } from '../../hooks/api/updateEntityDetails/useIdentificationUpdateEntityPayload'
import { useUpdateEntityDetails } from '../../hooks/api/updateEntityDetails/useUpdateEntityDetails'
import { useAdrum } from '../../hooks/useAdrum'
import { useNationality } from '../../hooks/useNationality'
import { t } from '../../i18n'
import { ButtonSaveContainer } from './style'

export const Identification = () => {
  const [showJob, setShowJob] = useState(false)
  const [enableAdrum, setEnableAdrum] = useState(true)
  const {
    policyholderDetailsSelected,
    professionSelected,
    setProfessionSelected,
  } = usePolicyholderDetailsStore()
  const [newJob, setNewJob] = useState<Result | null>(
    professionSelected || null,
  )
  const { setWarningOptions } = useWarningStore()
  const { policyHolderReferenceData } = useLoadPolicyHolderReferenceDataStore()
  const { policyholderAdditionalDetails } = useGetAdditionalEntityDetailsStore()
  const { handleIdentificationPayload } = useIdentificationUpdateEntityPayload()
  const { mutate, isSuccess, isLoading } = useUpdateEntityDetails()
  const { getNationality } = useNationality()

  const { startADRUMTagging } = useAdrum(!enableAdrum)

  const getGender = (genderId: string) =>
    policyHolderReferenceData?.genders?.find(
      (gender) => gender.idElement === genderId,
    )

  const formattedProfessionsLoadData =
    policyHolderReferenceData?.professions?.map((profession) => ({
      caption: profession.description,
      value: profession.idElement,
    }))

  const formattedPolicyholderProfessions =
    policyholderAdditionalDetails?.professionList?.map((profession) => ({
      caption: profession.professionDescription,
      value: profession.professionCode,
    }))

  useEffect(() => {
    if (isSuccess) {
      setWarningOptions({
        type: WARNING_BAR.type.success,
        labelTypes: [t('pages.step3.success.update')],
      })
      setShowJob(false)
    }
  }, [isSuccess, setWarningOptions])

  useEffect(() => {
    if ((policyholderAdditionalDetails?.professionList || [])?.length === 0) {
      setShowJob(true)
    }
  }, [policyholderAdditionalDetails])

  useEffect(() => {
    if (enableAdrum && isSuccess) {
      startADRUMTagging(ADRUM_EVENT.identification)
      setEnableAdrum(false)
    }
  }, [enableAdrum, isSuccess])

  return (
    <>
      <MainLoader show={isLoading} />
      <Section
        id="identificationTitle"
        name={t('pages.step3.identification.generalData')}
        config={{ labelSize: '20px', labelBold: '500' }}
        colorTheme="blue"
      />
      <TextField
        readOnly
        label={t('pages.step3.identification.nif')}
        value={policyholderDetailsSelected?.vatNumber}
      />
      <TextField
        readOnly
        label={t('pages.step3.identification.clientType')}
        value={policyholderDetailsSelected?.entityType || ''}
      />
      <CheckBox
        readOnly
        maintainStyleOnReadOnly
        label={t('pages.step3.identification.eni')}
        value={policyholderAdditionalDetails?.eni ? '1' : '0'}
      />
      <TextField
        readOnly
        label={t('pages.step3.identification.name')}
        value={policyholderDetailsSelected?.name}
      />
      <TextField
        readOnly
        label={t('pages.step3.identification.birthdate')}
        value={moment(policyholderDetailsSelected?.birthdate).format(
          'DD-MM-YYYY',
        )}
      />
      <TextField
        readOnly
        label={t('pages.step3.identification.Nationality')}
        value={
          getNationality(policyholderAdditionalDetails?.nationality || '')
            ?.description || ''
        }
      />
      <TextField
        readOnly
        label={t('pages.step3.identification.gender')}
        value={
          getGender(policyholderAdditionalDetails?.gender || '')?.description ||
          ''
        }
      />
      <Section
        id="dataTitle"
        name={t('pages.step3.identification.additionalInformation')}
        config={{ labelSize: '20px', labelBold: '500' }}
        colorTheme="blue"
      />
      {(formattedPolicyholderProfessions || [])?.length > 0 && (
        <Dropdown
          id={`entityJob-${policyholderAdditionalDetails?.professionList?.length}`}
          key={`entityJob-${policyholderAdditionalDetails?.professionList?.length}`}
          name="entityJob"
          label={t('pages.step3.identification.profession')}
          value={
            professionSelected?.description ||
            policyholderAdditionalDetails?.professionList?.[0]
              ?.professionDescription ||
            ''
          }
          onValueChanged={(id: any, value: any) => {
            const newJob = policyHolderReferenceData?.professions?.find(
              (item) => item.idElement === value,
            )
            setNewJob(newJob as never)
            setProfessionSelected(newJob as never)
          }}
          colorTheme="blue"
          options={formattedPolicyholderProfessions}
          version="2"
        />
      )}
      {showJob && !isLoading && (
        <>
          <Dropdown
            required
            id="entityAddJob"
            name="entityAddJob"
            label={t('pages.step3.identification.newProfession')}
            onValueChanged={(id: any, value: any) => {
              const newJob = policyHolderReferenceData?.professions?.find(
                (item) => item.idElement === value,
              )
              setNewJob(newJob as never)
              setProfessionSelected(newJob as never)
            }}
            colorTheme="blue"
            options={formattedProfessionsLoadData}
            version="2"
          />
          {(formattedPolicyholderProfessions || [])?.length > 0 && (
            <div className="add-content">
              <Link
                label={t('general.cancel')}
                onValueChanged={() => setShowJob(false)}
              />
            </div>
          )}
          <ButtonSaveContainer>
            <div className="button-save">
              <Action
                id="identificationAction"
                label={t('general.save')}
                type="button"
                onValueChanged={() =>
                  mutate({
                    payload: handleIdentificationPayload(newJob as Result),
                    type: 'identification',
                  })
                }
              />
            </div>
          </ButtonSaveContainer>
        </>
      )}
      {!showJob && (
        <div className="add-content">
          <Link
            label={t('pages.step3.identification.addProfession')}
            onValueChanged={() => setShowJob(true)}
          />
        </div>
      )}
    </>
  )
}
